import { useState } from 'react'

import { createConnectQueryKey } from '@connectrpc/connect-query'
import type { ListBiddingIntradayOrdersResponse } from '@ebalance/proto/bffuser/v1/bidding_intraday_service_pb'
import { getWorkflowCase } from '@ebalance/proto/bffuser/v1/workflow_case_service-WorkflowCaseService_connectquery'
import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import { biddingQueryKeys } from '~/hooks/bidding/queryKeys'
import { useWithdrawBiddingIntradayOrders } from '~/hooks/bidding/useWithdrawBiddingIntradayOrders'
import { useErrorToast } from '~/hooks/useErrorToast'
import {
  useHoldIntradayBulkWithdrawNotices,
  useTrackIntradayBulkWithdraw,
} from '~/pages/IntradayBidding/hooks/useIntradayWithdrawNotice'
import type { IntradayBidRow } from '~/pages/IntradayBidding/models/intradayBidRow'
import {
  type IntradayBulkWithdrawOutcome,
  intradayBulkWithdrawBlockedCause,
  intradayBulkWithdrawPendingIds,
  intradayBulkWithdrawRequestFailure,
  intradayBulkWithdrawRequestTargets,
  intradayBulkWithdrawTargets,
  readIntradayBulkWithdrawResponse,
} from '~/pages/IntradayBidding/models/intradayBulkWithdrawTargets'

const KEY = 'page:biddingIntraday.bulkWithdraw'

/** 画面を開いたワークフローケース (読み取り専用かの判定の元。 AC-WFW-014)。 */
const WORKFLOW_CASE_KEY = createConnectQueryKey({
  schema: getWorkflowCase,
  cardinality: 'finite',
})

/**
 * 画面で読んでいる時間前の一覧 (active な query) の札。 mutation hook が失敗の後にも一覧を読み直して
 * から失敗を返すので、 読み直した後の値になる。
 */
const activeIntradayListItems = (
  queryClient: QueryClient,
): ListBiddingIntradayOrdersResponse['items'] =>
  queryClient
    .getQueriesData<ListBiddingIntradayOrdersResponse>({
      queryKey: biddingQueryKeys.intradayList,
      type: 'active',
    })
    .flatMap(([, data]) => data?.items ?? [])

/** 受け付けなかった札のバナーの中身。 受渡日ごとの結果なので受渡日を持つ。 */
export type IntradayBulkWithdrawRefused = NonNullable<
  IntradayBulkWithdrawOutcome['refused']
> & { targetDate: Date }

/** 入札中の札の一括板取消 (AC-BIN-112) の、 ⋯メニュー項目・確認ダイアログ・バナーの状態。 */
export type IntradayBulkWithdraw = {
  /** ⋯メニュー項目を選べるか。 */
  selectable: boolean
  /**
   * 項目の hover / 強調で出すツールチップ。 選べる時は対象の件数、 選べない時は理由
   * (一覧の取得前・読み取り専用の理由の取得中は理由なし = undefined)。
   */
  tooltip: string | undefined
  /** 確認ダイアログを開く (対象をこの時点の一覧で固定する)。 */
  open: () => void
  /** 確認ダイアログで確認している対象。 undefined の間は閉じている。 */
  confirmTargets: readonly IntradayBidRow[] | undefined
  isSubmitting: boolean
  confirm: () => Promise<void>
  cancel: () => void
  /** 受け付けなかった札のバナー。 */
  refused: IntradayBulkWithdrawRefused | undefined
  dismissRefused: () => void
}

type Params = {
  /** 入札一覧の絞り込み (AC-BIN-102 + AC-BIN-018) の後の札。 一覧の取得前は空。 */
  filteredBids: readonly IntradayBidRow[]
  /** 一覧を取得できているか (初めての読み込み中・取得の失敗は false)。 */
  isListLoaded: boolean
  /** 完了済み・削除済みのワークフローから開いている (AC-WFW-014)。 */
  isReadonly: boolean
  /** 読み取り専用の理由 (`useWorkflowLockReasons`。 取得中は undefined)。 */
  readonlyReason: string | undefined
  targetDate: Date
}

/**
 * 入札中の札の一括板取消 (AC-BIN-112)。
 *
 * - 対象は絞り込み後の札のうち入札中で 1 件の取消ボタンが出る札。 確認ダイアログを開いた時点の
 *   一覧で固定し (id と updatedAt の組)、 開いている間に一覧が変わっても {N} と送る札は変えない
 * - 受け付けた札は結果を件数のトーストで知らせる (`useTrackIntradayBulkWithdraw`)。
 *   受け付けなかった札があればエラーバナー (次の一括で置き換える)
 * - 要求の全体を受け付けなかった時はエラーのトーストだけを出す (ワークフローが完了・削除されていた
 *   時はケースも読み直して画面を読み取り専用にする)。 通信の失敗で受け付けたかが分からない時は、
 *   読み直した一覧で取消処理中の札 (受け付けられていた札) だけを追う。 どちらもバナーは出さず、
 *   やり直しは促さない (一覧の読み直しは mutation hook が行う)
 */
export function useIntradayBulkWithdraw({
  filteredBids,
  isListLoaded,
  isReadonly,
  readonlyReason,
  targetDate,
}: Params): IntradayBulkWithdraw {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const showErrorToast = useErrorToast()
  const trackBulk = useTrackIntradayBulkWithdraw()
  const holdNotices = useHoldIntradayBulkWithdrawNotices()
  const { mutateAsync, isPending } = useWithdrawBiddingIntradayOrders()
  const [confirmTargets, setConfirmTargets] = useState<
    readonly IntradayBidRow[] | undefined
  >(undefined)
  const [refused, setRefused] = useState<
    IntradayBulkWithdrawRefused | undefined
  >(undefined)

  const targets = intradayBulkWithdrawTargets(filteredBids)
  const blockedCause = intradayBulkWithdrawBlockedCause(filteredBids)
  const selectable = !isReadonly && isListLoaded && blockedCause === undefined

  // 複数に当たる時は読み取り専用 → すべて取消処理中 → 0 件の順で 1 つだけ出す。 一覧の取得前は
  // 件数が決まらないので理由を出さない (一覧の読み込み中・取得失敗の表示に任せる)。
  const tooltip = isReadonly
    ? readonlyReason
    : !isListLoaded
      ? undefined
      : blockedCause === 'allPending'
        ? t(`${KEY}.menu.allPendingReason`)
        : blockedCause === 'noTarget'
          ? t(`${KEY}.menu.noTargetReason`)
          : t(`${KEY}.menu.tooltip`, { count: targets.length })

  const confirm = async (): Promise<void> => {
    const sending = confirmTargets
    if (sending === undefined || sending.length === 0) return
    // 前の一括のバナーは、 次の一括で置き換える (要求の全体が失敗した時も古い結果を残さない)。
    setRefused(undefined)
    // 応答を待つ間の一覧でも、 送った札に札ごとの「取消を送りました」 を出さない。
    const settleHeld = holdNotices(sending.map((bid) => bid.id))
    try {
      const response = await mutateAsync({
        targets: intradayBulkWithdrawRequestTargets(sending),
      })
      const outcome = readIntradayBulkWithdrawResponse(response, sending)
      trackBulk(outcome.acceptedIds)
      settleHeld(outcome.acceptedIds)
      setRefused(
        outcome.refused === undefined
          ? undefined
          : { ...outcome.refused, targetDate },
      )
    } catch (error) {
      const failure = intradayBulkWithdrawRequestFailure(error)
      if (failure === 'unknown') {
        // 読み直した一覧で取消処理中の札は受け付けられていたので、 その結果を件数で知らせる。
        const pendingIds = intradayBulkWithdrawPendingIds(
          activeIntradayListItems(queryClient),
          sending.map((bid) => bid.id),
        )
        trackBulk(pendingIds)
        settleHeld(pendingIds)
        showErrorToast({
          title: t(`${KEY}.resultUnknown.title`),
          description: t(`${KEY}.resultUnknown.body`),
        })
      } else {
        settleHeld([])
        if (failure === 'workflowClosed') {
          void queryClient.invalidateQueries({ queryKey: WORKFLOW_CASE_KEY })
        }
        showErrorToast({
          title: t('page:biddingIntraday.withdrawConfirm.error'),
          description: t(
            failure === 'workflowClosed'
              ? `${KEY}.requestFailed.workflowClosed`
              : `${KEY}.requestFailed.other`,
          ),
        })
      }
    }
    setConfirmTargets(undefined)
  }

  return {
    selectable,
    tooltip,
    open: () => {
      if (!selectable) return
      setConfirmTargets(targets)
    },
    confirmTargets,
    isSubmitting: isPending,
    confirm,
    cancel: () => {
      if (isPending) return
      setConfirmTargets(undefined)
    },
    refused,
    dismissRefused: () => setRefused(undefined),
  }
}
